import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../services/authentication.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    /**
     * The APIs allowed to receive the bearer token.
     *
     * An allowlist rather than "any request", because the app also talks to
     * api.indiapicture.in, admin.indiapicture.com and submission.mahatta.com for
     * search, banners and contributor data. Attaching the token to those would
     * hand our session credential to hosts that have no business holding it.
     *
     * All three of our own backends are listed. Only environment.backendUrl used
     * to be, so calls to the exclusive and budget backends went out with no
     * Authorization header at all — which is why routes on those prefixes could
     * not be placed behind jwt.verify without breaking the app.
     */
    private readonly authorizedApis = [
        environment.backendUrl,
        environment.exclusiveBackendUrl,
        environment.budgetBackendUrl,
    ].filter(url => !!url);

    constructor(private authenticationService: AuthenticationService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.authenticationService.currentUserValue;

        if (currentUser && currentUser.token && this.isOwnApi(request.url)) {
            request = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${currentUser.token}`
                }
            });
        }

        return next.handle(request);
    }

    /**
     * Prefix match, not substring.
     *
     * This used `indexOf(api) > -1`, which matches the URL *anywhere* -- so a
     * request to a third-party host that happened to carry one of our API URLs in
     * a query parameter or a redirect target (`?next=https://indiapicture.com/api/...`)
     * satisfied the check and had the bearer token attached. That is precisely the
     * leak the allowlist was added to prevent, so the comparison has to be
     * anchored at the start of the URL.
     */
    private isOwnApi(url: string): boolean {
        return this.authorizedApis.some(api => url.startsWith(api));
    }
}
